import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useResolvedPreparerId } from '../hooks/useResolvedPreparerId';
import { useCurrentUser } from '../hooks/useCurrentUser';
import {
  Spinner,
  Dropdown,
  Option,
  Button,
  Badge,
  Tooltip,
  Checkbox,
  makeStyles,
  tokens,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Input,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
} from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  ArrowDownloadRegular,
  PeopleRegular,
  DismissRegular,
  SelectAllOnRegular,
  PersonDeleteRegular,
  DeleteRegular,
  PersonAvailableRegular,
  SearchRegular,
  ColumnTripleRegular,
  EyeRegular,
  DocumentArrowDownRegular,
  BookmarkRegular,
  SaveRegular,
  StarFilled,
  StarRegular,
} from '@fluentui/react-icons';
import DataTable, { type Column } from '../components/DataTable';
import { RowLink } from '../components/links/EntityLink';
import { useListSearchParams } from '../components/listFilters/useListSearchParams';
import { useTaxReturnsScopeParams } from './useTaxReturnsScopeParams';
import { isScheduledToday } from './isScheduledToday';
import { applyClientFilters } from '../components/listFilters/applyClientFilters';
import { computeFacetCounts, computeNoValueCount } from '../components/listFilters/facetCounts';
import { NO_VALUE_TOKEN } from '../components/listFilters/columnFilterOps';
import { ColumnHeaderFilter } from '../components/listFilters/ColumnHeaderFilter';
import { ColumnMenu } from '../components/listFilters/ColumnMenu';
import { ActiveFiltersChips } from '../components/listFilters/ActiveFiltersChips';
import { QuickViewPills, type QuickView } from '../components/listFilters/QuickViewPills';
import type { FilterColumn } from '../components/listFilters/types';
import StatusBadge from '../components/StatusBadge';
import { format } from 'date-fns';
import { getTaxReturns, importAppointments, getAppointmentAttendees, syncClientInfo, bulkUpdateTaxReturnStatus, deleteTaxReturn, updateTaxReturn, updateTaxReturnStatus, syncXeroPayments } from '../services/api';
import {
  statusDisplayNames,
  appointmentTypeDisplayNames,
  type TaxReturn,
  type TaxReturnStatus,
  type AppointmentAttendeesResponse,
  amendmentBadgeLabel,
  amendmentToggleLabel,
} from '../types';
import AttendeeResolutionDialog from '../components/AttendeeResolutionDialog';
import CreateDropoffReturnDialog from '../components/CreateDropoffReturnDialog';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { exportToExcel, dateStampedFilename, type ExcelExportColumn } from '../utils/excelExport';
import {
  useSavedViews,
  useCreateSavedView,
  useDeleteSavedView,
  useSetSavedViewDefault,
  CRM_VIEWS_APP_ID,
  type SavedViewDto,
} from '../services/crmViewsApi';
import { useScreenStateDefault } from '../hooks/useScreenStateDefault';
import { decodeQueryState, encodeQueryState, splitStaleFilters } from '../utils/screenStateQuery';
import { useNotificationToast } from '../hooks/useNotificationToast';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1; // Tax prep year (preparing prior year's taxes)
const yearOptions = [defaultTaxYear, defaultTaxYear - 1, defaultTaxYear - 2];

const statusOptions: TaxReturnStatus[] = [
  'scheduled',
  'pending_resolution',
  'documents_pending',
  'ready_to_prepare',
  'in_preparation',
  'in_review',
  'needs_corrections',
  'ready_for_signature',
  'waiting_for_payment',
  'ready_for_efile',
  'e_filed',
  'completed',
  'inactive',
];

// Status sort order (workflow progression)
const statusSortOrder: Record<TaxReturnStatus, number> = {
  scheduled: 1,
  pending_resolution: 2,
  documents_pending: 3,
  ready_to_prepare: 4,
  in_preparation: 5,
  in_review: 6,
  needs_corrections: 7,
  ready_for_signature: 8,
  waiting_for_payment: 9,
  ready_for_efile: 10,
  e_filed: 11,
  completed: 12,
  inactive: 13,
};

// Column keys shown by the broad views. Reviewer + Amendment are new (Spec 2): reviewerName ships
// on every row already, and amendment filtering moves from a toolbar dropdown to a header funnel.
const ALL_COLUMN_KEYS = ['clientName', 'taxYear', 'appointmentType', 'isAmendment', 'status', 'efileStatus', 'preparerName', 'reviewerName', 'scheduledDate', 'allDocumentsReceived', 'paymentStatus'];

// Reconcile the "Pending Payment" view against Xero when it is opened. See the sync effect below.
const PENDING_PAYMENT_SYNC_COOLDOWN_MS = 2 * 60 * 1000;
let lastPendingPaymentSyncAt = 0;

// Column catalog for the column chooser popover. Keys only — the display header is translated,
// computed inside the component as `columnLabels` (see below), keyed by the same strings.
const COLUMN_DEFINITIONS = ALL_COLUMN_KEYS;

interface PresetView {
  id: string;
  /** i18n key under taxReturnsPage.views.* — labels are translated, so the display text lives in
   * the locale files, not here. See QUICK_VIEWS below, built inside the component with t(). */
  labelKey: string;
  visibleColumns: string[];
}

// The preset views (8 from Spec 2, plus Today's My Returns — see isScheduledToday.ts). Rendered as
// QuickViewPills; the id order is also the default landing order. "Today's My Returns" is placed
// FIRST, ahead of the broader "My Returns" — it's the first smart-default-view step toward the
// fleet filter contract's §5.1.1 staff-interactive presets; it is NOT yet the default landing view
// (DEFAULT_VIEW below is unchanged) because switching every preparer's landing view is a workflow
// decision bigger than this one pill, and belongs with the presets-wave rebase + per-user
// preference work tracked separately.
const PRESET_VIEWS: PresetView[] = [
  { id: 'today-my-returns', labelKey: 'taxReturnsPage.views.todayMyReturns', visibleColumns: ALL_COLUMN_KEYS },
  { id: 'my-returns', labelKey: 'taxReturnsPage.views.myReturns', visibleColumns: ALL_COLUMN_KEYS },
  { id: 'all', labelKey: 'taxReturnsPage.views.all', visibleColumns: ALL_COLUMN_KEYS },
  { id: 'ready-review', labelKey: 'taxReturnsPage.views.readyReview', visibleColumns: ['clientName', 'status', 'preparerName', 'reviewerName', 'scheduledDate'] },
  { id: 'ready-efile', labelKey: 'taxReturnsPage.views.readyEfile', visibleColumns: ['clientName', 'status', 'preparerName', 'reviewerName', 'scheduledDate'] },
  { id: 'pending-payment', labelKey: 'taxReturnsPage.views.pendingPayment', visibleColumns: ['clientName', 'status', 'preparerName', 'scheduledDate'] },
  { id: 'pending-efile-response', labelKey: 'taxReturnsPage.views.pendingEfileResponse', visibleColumns: ['clientName', 'status', 'efileStatus', 'preparerName', 'reviewerName', 'scheduledDate'] },
  { id: 'in-progress', labelKey: 'taxReturnsPage.views.inProgress', visibleColumns: ALL_COLUMN_KEYS },
  { id: 'dropoff-pool', labelKey: 'taxReturnsPage.views.dropoffPool', visibleColumns: ['clientName', 'taxYear', 'status', 'scheduledDate', 'allDocumentsReceived'] },
];

const DEFAULT_VIEW = 'my-returns';
const PRESET_BY_ID = new Map(PRESET_VIEWS.map((v) => [v.id, v]));

const bulkStatusOptions: TaxReturnStatus[] = [
  'documents_pending',
  'ready_to_prepare',
  'in_preparation',
  'in_review',
  'ready_for_signature',
  'waiting_for_payment',
  'ready_for_efile',
  'e_filed',
  'completed',
  'inactive',
];

// Stable ref (a fresh array each render would retrigger DataTable's processed-data memo → loop).
const SEARCH_KEYS: (keyof TaxReturn)[] = ['clientName', 'clientEmail', 'searchableAliases'];

const efileStatusOptions = ['pending', 'submitted', 'accepted', 'rejected'];

const useBulkStyles = makeStyles({
  bulkBar: {
    position: 'sticky',
    bottom: '0',
    left: '0',
    right: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 24px',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: '8px 8px 0 0',
    boxShadow: tokens.shadow16,
    zIndex: 100,
  },
  bulkCount: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    minWidth: '120px',
  },
  bulkMessage: {
    padding: '12px 16px',
    marginBottom: '16px',
    borderRadius: '4px',
    fontSize: tokens.fontSizeBase200,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  toolbarSpacer: {
    flex: 1,
    minWidth: '8px',
  },
  // Search + tax-year sit at the START of the toolbar, ahead of the quick-view pills. They are
  // grouped so they relocate (and wrap) as a unit rather than being split across toolbar rows.
  // Own flexWrap: at very narrow widths the year drops under the search instead of forcing the
  // group wider than the viewport — the search keeps its 260px floor either way.
  leadingFilters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
  },
});

const COL_STORAGE_KEY = 'taxReturns-columns';

function loadColumnOverrides(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(COL_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

export default function TaxReturnsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const bulkStyles = useBulkStyles();
  const { preparerId: currentUserId, preparerName: currentUserName } = useCurrentUser();

  // Translated display strings for data-driven, module-scope catalogs (PRESET_VIEWS,
  // COLUMN_DEFINITIONS, efileStatusOptions) — those stay as plain ids/keys at module scope since
  // they can't call t(), and are turned into display labels here instead.
  const QUICK_VIEWS: QuickView[] = useMemo(
    () => PRESET_VIEWS.map((v) => ({ key: v.id, label: t(v.labelKey) })),
    [t]
  );
  const columnLabels: Record<string, string> = useMemo(() => ({
    clientName: t('taxReturnsPage.columns.client'),
    taxYear: t('taxReturnsPage.columns.taxYear'),
    appointmentType: t('taxReturnsPage.columns.type'),
    isAmendment: t('taxReturnsPage.columns.amendment'),
    status: t('taxReturnsPage.columns.status'),
    efileStatus: t('taxReturnsPage.columns.efile'),
    preparerName: t('taxReturnsPage.columns.preparer'),
    reviewerName: t('taxReturnsPage.columns.reviewer'),
    scheduledDate: t('taxReturnsPage.columns.scheduled'),
    allDocumentsReceived: t('taxReturnsPage.columns.documents'),
    paymentStatus: t('taxReturnsPage.columns.payment'),
  }), [t]);
  const efileStatusLabels: Record<string, string> = useMemo(() => ({
    pending: t('taxReturnsPage.efileStatusLabels.pending'),
    submitted: t('taxReturnsPage.efileStatusLabels.submitted'),
    accepted: t('taxReturnsPage.efileStatusLabels.accepted'),
    rejected: t('taxReturnsPage.efileStatusLabels.rejected'),
  }), [t]);

  // Filter / sort / view / page state lives in the URL (shared fleet codec).
  const list = useListSearchParams(DEFAULT_VIEW);
  const activeViewId = list.view;

  // Scope state — year, inactive-visibility and search text — ALSO lives in the URL, in
  // page-local params, so back-navigation and refresh restore it and a filtered view is
  // shareable by link. Kept out of the shared codec deliberately: see useTaxReturnsScopeParams.
  const {
    year: selectedYear,
    inactive: showInactive,
    q: searchText,
    setYear: setSelectedYear,
    setInactive: setShowInactive,
    setQ: setSearchText
  } = useTaxReturnsScopeParams(defaultTaxYear);
  const { resolvedPreparerId, preparerId, preparerName, isLoading: userLoading } = useResolvedPreparerId(selectedYear);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [initialFilterApplied, setInitialFilterApplied] = useState(false);
  const [showDropoffDialog, setShowDropoffDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TaxReturn | null>(null);

  // Column visibility: per-view overrides on top of the preset defaults (localStorage-persisted).
  const [columnOverrides, setColumnOverrides] = useState<Record<string, string[]>>(loadColumnOverrides);
  const presetColumns = PRESET_BY_ID.get(activeViewId)?.visibleColumns ?? ALL_COLUMN_KEYS;
  const visibleColumns = columnOverrides[activeViewId] ?? presetColumns;

  const toggleColumn = useCallback((colKey: string) => {
    const current = [...(columnOverrides[activeViewId] ?? (PRESET_BY_ID.get(activeViewId)?.visibleColumns ?? ALL_COLUMN_KEYS))];
    const idx = current.indexOf(colKey);
    if (idx >= 0) {
      if (current.length <= 1) return; // never hide every column
      current.splice(idx, 1);
    } else {
      current.push(colKey);
    }
    setColumnOverrides((prev) => {
      const next = { ...prev, [activeViewId]: current };
      try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [activeViewId, columnOverrides]);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<TaxReturnStatus | ''>('');
  const [bulkEfileStatus, setBulkEfileStatus] = useState<string>('');
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [pageItems, setPageItems] = useState<TaxReturn[]>([]);

  // Resolution dialog state
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [attendeesData, setAttendeesData] = useState<AppointmentAttendeesResponse | null>(null);
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  const { data: taxReturns, isLoading, error, refetch } = useQuery<TaxReturn[]>({
    queryKey: ['taxReturns', selectedYear],
    // Year is the only server-side scope; every other filter is applied client-side (fetch-all).
    queryFn: () => getTaxReturns({ taxYear: selectedYear }),
    enabled: initialFilterApplied,
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  // View-predicate layer: the 8 presets' EXACT predicates + the show-inactive scope. Ad-hoc column
  // filtering is layered on top via applyClientFilters (URL `filter.*`).
  const viewFiltered = useMemo(() => {
    return (taxReturns || []).filter((tr) => {
      if (!showInactive && tr.status === 'inactive') return false;
      switch (activeViewId) {
        case 'today-my-returns':
          // Same preparer scope as "My Returns", narrowed to today's scheduledDate (staff-
          // interactive, resolved in America/New_York — see isScheduledToday.ts).
          if (resolvedPreparerId && tr.preparerId !== resolvedPreparerId) return false;
          if (!isScheduledToday(tr.scheduledDate)) return false;
          break;
        case 'my-returns':
          // Caller-scoped: only when the signed-in user resolves to a preparer (else show all).
          if (resolvedPreparerId && tr.preparerId !== resolvedPreparerId) return false;
          break;
        case 'ready-review':
          if (tr.status !== 'in_review') return false;
          break;
        case 'ready-efile':
          if (tr.status !== 'ready_for_efile') return false;
          break;
        case 'pending-payment':
          if (tr.clientInvoice?.status !== 'generated') return false;
          // A return can be paid off through a shared/bulk invoice allocation without its OWN
          // clientInvoice.status ever moving off 'generated' — invoiceAllocationPaid is the other
          // signal every other "is this paid?" check in this app already looks at (see the sort
          // comparator below and TaxReturnDetailPage's e-file gate). Missing it here is why a paid
          // return could still show under Pending Payment.
          if (tr.invoiceAllocationPaid) return false;
          break;
        case 'pending-efile-response':
          // Mirrors backend TaxReturnEFile.IsAwaitingResponse.
          if (!(tr.status === 'e_filed' && tr.efileStatus !== 'accepted' && tr.efileStatus !== 'rejected')) return false;
          break;
        case 'in-progress':
          // Matches DashboardFunctions.cs's own "In Progress" stat (InProgress = count of
          // status === InPreparation) rather than the broad "not done yet" complement this used to
          // be. The two surfaces disagreeing about what "In Progress" means — this page's old
          // definition included ready_for_efile, waiting_for_payment, in_review, etc. — is the
          // defect: a staff member sees one number on the dashboard and a different, larger one
          // behind this quick-view pill for a label that reads as the same thing.
          if (tr.status !== 'in_preparation') return false;
          break;
        case 'dropoff-pool':
          if (!(tr.appointmentType === 'drop-off' && tr.status === 'ready_to_prepare' && !tr.preparerId)) return false;
          break;
        default:
          break;
      }
      return true;
    });
  }, [taxReturns, showInactive, activeViewId, resolvedPreparerId]);

  // Distinct preparer / reviewer names present in the fetched data — populate the picklist filters.
  const preparerFilterOptions = useMemo(() => {
    const names = new Set<string>();
    for (const tr of taxReturns || []) if (tr.preparerName) names.add(tr.preparerName);
    return [...names].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
  }, [taxReturns]);

  const reviewerFilterOptions = useMemo(() => {
    const names = new Set<string>();
    for (const tr of taxReturns || []) if (tr.reviewerName) names.add(tr.reviewerName);
    return [...names].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
  }, [taxReturns]);

  // The ONE column catalog: the header filters + chips render it, and applyClientFilters filters
  // by it (BL-141). It replaced a separate FILTER_FIELDS map keyed by the same strings — two lists
  // that had to agree on every key, with nothing to notice when they did not.
  const filterColumns = useMemo<FilterColumn<TaxReturn>[]>(() => [
    { key: 'clientName', label: columnLabels.clientName, filterType: 'text', getValue: (tr) => tr.clientName },
    { key: 'appointmentType', label: columnLabels.appointmentType, filterType: 'picklist', filterOptions: (['in-person', 'virtual', 'drop-off'] as const).map((v) => ({ value: v, label: appointmentTypeDisplayNames[v] })), getValue: (tr) => tr.appointmentType },
    { key: 'isAmendment', label: columnLabels.isAmendment, filterType: 'boolean', getValue: (tr) => tr.isAmendment ?? false },
    { key: 'status', label: columnLabels.status, filterType: 'picklist', filterOptions: statusOptions.map((s) => ({ value: s, label: statusDisplayNames[s] })), getValue: (tr) => tr.status },
    { key: 'efileStatus', label: columnLabels.efileStatus, filterType: 'picklist', filterOptions: efileStatusOptions.map((s) => ({ value: s, label: efileStatusLabels[s] })), getValue: (tr) => tr.efileStatus },
    { key: 'preparerName', label: columnLabels.preparerName, filterType: 'picklist', filterOptions: preparerFilterOptions, getValue: (tr) => tr.preparerName },
    { key: 'reviewerName', label: columnLabels.reviewerName, filterType: 'picklist', filterOptions: reviewerFilterOptions, getValue: (tr) => tr.reviewerName },
    { key: 'scheduledDate', label: columnLabels.scheduledDate, filterType: 'date-range', getValue: (tr) => tr.scheduledDate },
    { key: 'allDocumentsReceived', label: columnLabels.allDocumentsReceived, filterType: 'boolean', getValue: (tr) => tr.allDocumentsReceived },
  ], [preparerFilterOptions, reviewerFilterOptions, columnLabels, efileStatusLabels]);

  const displayData = useMemo(
    () => applyClientFilters(viewFiltered, list.filters, filterColumns),
    [viewFiltered, list.filters, filterColumns]
  );

  const columnMenuLabels = useMemo(() => ({
    sortAZ: t('common.columnMenu.sortAZ'),
    sortZA: t('common.columnMenu.sortZA'),
    groupByHeader: t('common.columnMenu.groupBy'),
    clearGrouping: t('common.columnMenu.clearGrouping'),
    granularity: {
      year: t('common.columnMenu.granularity.year'),
      quarter: t('common.columnMenu.granularity.quarter'),
      month: t('common.columnMenu.granularity.month'),
      week: t('common.columnMenu.granularity.week'),
      day: t('common.columnMenu.granularity.day'),
    },
    triggerLabel: t('common.columnMenu.trigger'),
  }), [t]);

  // Group-by (fleet filter contract v3 §13.3-13.4, M1/M3) is offered ONLY on `scheduledDate` --
  // the sole date-range column in filterColumns above; every other column correctly omits the
  // "Group by" section (C66: a column without group support is conformant, not a gap).
  const GROUP_COLUMN_KEY = 'scheduledDate';

  const renderHeaderExtra = useCallback((key: string) => {
    const filterCol = filterColumns.find((c) => c.key === key);
    const isSortable = ALL_COLUMN_KEYS.includes(key);

    const menu = isSortable ? (
      <ColumnMenu
        columnLabel={columnLabels[key] ?? key}
        onSort={(direction) => list.setSort({ col: key, dir: direction })}
        labels={columnMenuLabels}
        group={key === GROUP_COLUMN_KEY ? {
          active: list.group === GROUP_COLUMN_KEY,
          granularity: list.group === GROUP_COLUMN_KEY ? list.groupGranularity : null,
          onSetGroup: (granularity) => list.setGroup(GROUP_COLUMN_KEY, granularity),
          onClearGroup: () => list.clearGroup(),
        } : undefined}
      />
    ) : null;

    if (!filterCol) return menu;
    return (
      <>
        {menu}
        <ColumnHeaderFilter
          column={filterCol}
          value={list.filters}
          onChange={list.setFilters}
          // C86 / §16.3: this page hadn't adopted facets yet (see ColumnHeaderFilter.tsx's own
          // comment, now stale) -- wiring it here is part of applying the lookup combobox to every
          // lookup column on every list, not just Outreach.
          getFacetCounts={() => ({
            ...computeFacetCounts(viewFiltered, list.filters, filterColumns, filterCol),
            [NO_VALUE_TOKEN]: computeNoValueCount(viewFiltered, list.filters, filterColumns, filterCol),
          })}
        />
      </>
    );
  }, [filterColumns, list, viewFiltered, columnLabels, columnMenuLabels]);

  // Wave 3 (2026-11-02): saved views + the per-person landing default, adopted here for the first
  // time (this page had neither before) on the SAME real stores Client Outreach just moved onto
  // (filter contract v3 §16.1-16.2) -- not an interim, since the stores are already live. Remembers
  // ONLY `q` + `filter.*` (never `view`/`year`/`inactive`/`sort`/`dir`/`page`), matching Outreach's
  // own scope exactly: `view` already resolves "my returns" through a quick-view KEY, not a
  // caller-scoped VALUE sitting in a `filter.*` param, so there is nothing here that needs an
  // isCallerScoped predicate the way Outreach's "my clients" (`filter.preferredPreparerId.eq`) did.
  const TAX_RETURNS_SCREEN_ID = 'tax-returns';
  const { showToast: showViewsToast } = useNotificationToast();
  const taxReturnsSavedViewsQuery = useSavedViews(CRM_VIEWS_APP_ID, TAX_RETURNS_SCREEN_ID);
  const createTaxReturnsView = useCreateSavedView();
  const deleteTaxReturnsView = useDeleteSavedView();
  const setDefaultTaxReturnsView = useSetSavedViewDefault();
  const taxReturnsSavedViews = taxReturnsSavedViewsQuery.data?.items ?? [];
  const defaultTaxReturnsView = taxReturnsSavedViews.find((v) => v.isDefault);
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [newViewShared, setNewViewShared] = useState(false);

  const validTaxReturnsFilterColumnKeys = useMemo(() => new Set(filterColumns.map((c) => c.key)), [filterColumns]);

  // ONE atomic URL write, deliberately NOT `list.setFilters` + `setSearchText` in sequence: two
  // independent `useSearchParams()`-backed hooks each calling `setSearchParams` on the same URL in
  // the same tick is the exact last-write-wins race `useOutreachScopeParams.ts`'s own doc comment
  // names (and this page reproduced it: a first draft doing both calls dropped the filter change
  // under test). A dedicated `useSearchParams()` instance here, used for this one interaction only,
  // removes the race by construction.
  const [, setTaxReturnsSearchParams] = useSearchParams();
  const applyTaxReturnsSavedView = useCallback((view: SavedViewDto) => {
    const decoded = decodeQueryState(view.stateQuery);
    const { valid, staleCount } = splitStaleFilters(decoded.filters, validTaxReturnsFilterColumnKeys);
    if (staleCount > 0) {
      showViewsToast(
        t('outreachPage.savedViews.staleNoticeTitle'),
        t('outreachPage.savedViews.staleNoticeBody', { count: staleCount }),
        'warning'
      );
    }
    setTaxReturnsSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key of [...next.keys()]) {
        if (key.startsWith('filter.') || key === 'q') next.delete(key);
      }
      for (const [key, value] of Object.entries(valid)) next.set(key, value);
      if (decoded.q) next.set('q', decoded.q);
      next.delete('page');
      return next;
    }, { replace: true });
  }, [setTaxReturnsSearchParams, validTaxReturnsFilterColumnKeys, showViewsToast, t]);

  const handleSaveTaxReturnsView = useCallback(() => {
    if (!newViewName.trim()) return;
    createTaxReturnsView.mutate(
      {
        app: CRM_VIEWS_APP_ID,
        screenKey: TAX_RETURNS_SCREEN_ID,
        name: newViewName.trim(),
        stateQuery: encodeQueryState({ q: searchText || undefined, filters: list.filters }),
        visibility: newViewShared ? 'team' : 'personal',
      },
      {
        onSuccess: () => {
          setSaveViewDialogOpen(false);
          setNewViewName('');
          setNewViewShared(false);
        },
        onError: () => showViewsToast(t('outreachPage.savedViews.saveFailedTitle'), t('outreachPage.savedViews.saveFailedBody'), 'error'),
      }
    );
  }, [newViewName, newViewShared, searchText, list.filters, createTaxReturnsView, showViewsToast, t]);

  const handleSetDefaultTaxReturnsView = useCallback((view: SavedViewDto) => {
    setDefaultTaxReturnsView.mutate({ app: CRM_VIEWS_APP_ID, screenKey: TAX_RETURNS_SCREEN_ID, savedViewId: view.isDefault ? null : view.id });
  }, [setDefaultTaxReturnsView]);

  const handleDeleteTaxReturnsView = useCallback((id: string) => {
    deleteTaxReturnsView.mutate(id);
  }, [deleteTaxReturnsView]);

  const isTaxReturnsListParam = useCallback((key: string) => key === 'q' || key.startsWith('filter.'), []);
  const { reset: resetTaxReturnsScreenState } = useScreenStateDefault({
    app: CRM_VIEWS_APP_ID,
    screen: TAX_RETURNS_SCREEN_ID,
    isListParam: isTaxReturnsListParam,
    prefer: { ready: !taxReturnsSavedViewsQuery.isLoading, query: defaultTaxReturnsView?.stateQuery ?? null },
  });

  // Enable queries as soon as user auth is loaded (don't wait for preparer resolution)
  useEffect(() => {
    if (!userLoading && !initialFilterApplied) {
      setInitialFilterApplied(true);
    }
  }, [userLoading, initialFilterApplied]);

  // When the Pending Payment view is opened, reconcile invoice payment status against Xero so
  // paid/voided invoices drop out of the list. Best-effort and fail-open.
  useEffect(() => {
    if (!initialFilterApplied || activeViewId !== 'pending-payment') return;
    const now = Date.now();
    if (now - lastPendingPaymentSyncAt < PENDING_PAYMENT_SYNC_COOLDOWN_MS) return;
    lastPendingPaymentSyncAt = now;

    let cancelled = false;
    syncXeroPayments()
      .then(() => {
        if (!cancelled) queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      })
      .catch(() => { /* Fail-open: keep showing current data. */ });
    return () => { cancelled = true; };
  }, [activeViewId, initialFilterApplied, queryClient]);

  const importMutation = useMutation({
    mutationFn: () => importAppointments({ taxYear: selectedYear }),
    onSuccess: (result) => {
      setImportMessage(t('taxReturnsPage.messages.imported', { imported: result.imported, skipped: result.skipped }));
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      setTimeout(() => setImportMessage(null), 5000);
    },
    onError: (error: Error) => {
      setImportMessage(t('taxReturnsPage.messages.error', { message: error.message }));
      setTimeout(() => setImportMessage(null), 5000);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => syncClientInfo(selectedYear),
    onSuccess: (result) => {
      setImportMessage(t('taxReturnsPage.messages.synced', { updated: result.updated }));
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      setTimeout(() => setImportMessage(null), 5000);
    },
    onError: (error: Error) => {
      setImportMessage(t('taxReturnsPage.messages.error', { message: error.message }));
      setTimeout(() => setImportMessage(null), 5000);
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (params: { ids: string[]; status?: TaxReturnStatus; efileStatus?: string }) =>
      bulkUpdateTaxReturnStatus(params.ids, {
        status: params.status,
        efileStatus: params.efileStatus,
        changedBy: currentUserId,
        changedByName: currentUserName,
      }),
    onSuccess: (result) => {
      const msg = t('taxReturnsPage.messages.bulkUpdated', { count: result.updated }) +
        (result.failed ? t('taxReturnsPage.messages.bulkFailed', { count: result.failed }) : '') +
        (result.skipped ? t('taxReturnsPage.messages.bulkSkipped', { count: result.skipped }) : '');
      setBulkMessage(msg);
      setSelectedIds(new Set());
      setBulkStatus('');
      setBulkEfileStatus('');
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      setTimeout(() => setBulkMessage(null), 5000);
    },
    onError: (error: Error) => {
      setBulkMessage(t('taxReturnsPage.messages.error', { message: error.message }));
      setTimeout(() => setBulkMessage(null), 5000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTaxReturn(id),
    onSuccess: () => {
      setDeleteTarget(null);
      setImportMessage(t('taxReturnsPage.messages.deleted'));
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      setTimeout(() => setImportMessage(null), 5000);
    },
    onError: (error: Error) => {
      setDeleteTarget(null);
      setImportMessage(t('taxReturnsPage.messages.error', { message: error.message }));
      setTimeout(() => setImportMessage(null), 5000);
    },
  });

  const claimMutation = useMutation({
    mutationFn: async (taxReturn: TaxReturn) => {
      await updateTaxReturn(taxReturn.id, {
        preparerId: currentUserId,
        preparerName: currentUserName,
      });
      await updateTaxReturnStatus(taxReturn.id, 'in_preparation', {
        changedBy: currentUserId,
        changedByName: currentUserName,
      });
    },
    onSuccess: () => {
      setImportMessage(t('taxReturnsPage.messages.claimed'));
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      setTimeout(() => setImportMessage(null), 5000);
    },
    onError: (error: Error) => {
      setImportMessage(t('taxReturnsPage.messages.errorClaiming', { message: error.message }));
      setTimeout(() => setImportMessage(null), 5000);
    },
  });

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // pageItems is refreshed on every table render (via onPageDataChange), so toggleSelectAll reads it
  // from a ref rather than closing over it — a pageItems-dependent callback in the columns memo would
  // feed the onPageDataChange → setPageItems → columns-rebuild loop.
  const pageItemsRef = useRef(pageItems);
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => { pageItemsRef.current = pageItems; }, [pageItems]);
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

  const toggleSelectAll = useCallback(() => {
    const items = pageItemsRef.current;
    if (!items.length) return;
    const allSelected = items.every(item => selectedIdsRef.current.has(item.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) items.forEach(item => next.delete(item.id));
      else items.forEach(item => next.add(item.id));
      return next;
    });
  }, []);

  const handleBulkApply = () => {
    if (selectedIds.size === 0 || (!bulkStatus && !bulkEfileStatus)) return;
    bulkMutation.mutate({
      ids: Array.from(selectedIds),
      status: bulkStatus ? (bulkStatus as TaxReturnStatus) : undefined,
      efileStatus: bulkEfileStatus || undefined,
    });
  };

  const clearFilters = () => {
    setSelectedYear(defaultTaxYear);
    setShowInactive(false);
    setSearchText('');
    list.setFilters({});
    list.selectView(DEFAULT_VIEW);
  };

  const handleMarkAsInactive = () => {
    if (selectedIds.size === 0) return;
    bulkMutation.mutate({
      ids: Array.from(selectedIds),
      status: 'inactive',
    });
  };

  // Clear selection when the scope/view/filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedYear, list.filters, activeViewId, showInactive]);

  // Are all items on current page selected?
  const allPageSelected = pageItems.length > 0 && pageItems.every(item => selectedIds.has(item.id));
  const somePageSelected = pageItems.some(item => selectedIds.has(item.id));

  // Real links (fleet ruling 2026-10-29): undefined for a row that opens the resolution dialog
  // instead of navigating -- DataTable's getRowHref then leaves that row on handleRowClick below,
  // and the clientName column renders plain text instead of a RowLink. useCallback with no deps
  // (it's pure) so its identity never changes and it can sit safely in the columns memo's deps.
  const getTaxReturnDetailHref = useCallback((taxReturn: TaxReturn): string | undefined =>
    taxReturn.status === 'pending_resolution' && taxReturn.returnType !== 'business'
      ? undefined
      : `/tax-returns/${taxReturn.id}`,
  []);

  // Define columns for DataTable. Memoized so a parent re-render (e.g. from onPageDataChange's
  // setPageItems) doesn't hand DataTable a new columns ref every render — which would retrigger its
  // processed-data memo and loop.
  // FB-11 (Emma Morales, 2026-10-30): mirrors the visible data columns above, in the same order,
  // same headers (columnLabels -- the same i18n keys the table itself renders), as plain text
  // instead of JSX. _select and _actions are UI-only and deliberately excluded.
  const exportColumns: ExcelExportColumn<TaxReturn>[] = useMemo(() => [
    {
      header: columnLabels.clientName,
      getValue: (tr) => tr.clientName + (tr.spouseName ? ` & ${tr.spouseName}` : ''),
    },
    { header: columnLabels.taxYear, getValue: (tr) => tr.taxYear },
    { header: columnLabels.appointmentType, getValue: (tr) => appointmentTypeDisplayNames[tr.appointmentType] || tr.appointmentType },
    { header: columnLabels.isAmendment, getValue: (tr) => (tr.isAmendment ? amendmentBadgeLabel(tr.returnType) : '') },
    { header: columnLabels.status, getValue: (tr) => statusDisplayNames[tr.status] || tr.status },
    {
      header: columnLabels.efileStatus,
      getValue: (tr) => {
        if (!tr.efileStatus || (tr.status !== 'e_filed' && tr.status !== 'completed' && tr.status !== 'inactive')) return '';
        return efileStatusLabels[tr.efileStatus] || tr.efileStatus;
      },
    },
    { header: columnLabels.preparerName, getValue: (tr) => tr.preparerName || '' },
    { header: columnLabels.reviewerName, getValue: (tr) => tr.reviewerName || '' },
    { header: columnLabels.scheduledDate, getValue: (tr) => tr.scheduledDate ? format(new Date(tr.scheduledDate), 'MMM d, yyyy') : '' },
    {
      header: columnLabels.allDocumentsReceived,
      getValue: (tr) => tr.allDocumentsReceived
        ? t('taxReturnsPage.documentsComplete')
        : t('taxReturnsPage.documentsMissing', { count: tr.missingDocuments?.length || 0 }),
    },
    {
      header: columnLabels.paymentStatus,
      getValue: (tr) => {
        if (tr.cashPaymentAmount && tr.cashPaymentAmount > 0) {
          const isZelle = tr.cashPaymentReference?.toLowerCase().includes('zelle');
          return isZelle ? t('taxReturnsPage.zelle') : t('taxReturnsPage.cash');
        }
        if (tr.clientInvoice?.status === 'paid' || tr.invoiceAllocationPaid) return t('taxReturnsPage.paid');
        if (tr.clientInvoice?.status === 'generated') return t('taxReturnsPage.invoiced');
        return t('taxReturnsPage.notInvoiced');
      },
    },
  ], [columnLabels, t, efileStatusLabels]);

  const handleExport = useCallback(() => {
    void exportToExcel(dateStampedFilename('tax-returns'), exportColumns, displayData);
  }, [exportColumns, displayData]);

  const columns: Column<TaxReturn>[] = useMemo(() => [
    {
      key: '_select',
      header: (
        <Checkbox
          checked={allPageSelected ? true : somePageSelected ? 'mixed' : false}
          onChange={toggleSelectAll}
        />
      ) as unknown as string,
      sortable: false,
      render: (taxReturn) => (
        <Checkbox
          checked={selectedIds.has(taxReturn.id)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelect(taxReturn.id);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: 'clientName',
      header: columnLabels.clientName,
      sortable: true,
      render: (taxReturn) => {
        const href = getTaxReturnDetailHref(taxReturn);
        return (
        <div>
          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {href ? <RowLink href={href}>{taxReturn.clientName}</RowLink> : taxReturn.clientName}
            {taxReturn.filingType === 'joint' && (
              <Tooltip content={t('taxReturnsPage.jointFilingWith', { spouse: taxReturn.spouseName || t('taxReturnsPage.spouseFallback') })} relationship="label">
                <Badge appearance="outline" color="brand" icon={<PeopleRegular />}>
                  {t('taxReturnsPage.mfj')}
                </Badge>
              </Tooltip>
            )}
            {taxReturn.isAmendment && (
              <Tooltip content={amendmentToggleLabel(taxReturn.returnType, taxReturn.businessEntityType)} relationship="label">
                <Badge appearance="outline" color="danger">
                  {amendmentBadgeLabel(taxReturn.returnType)}
                </Badge>
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
            {taxReturn.clientEmail}
            {taxReturn.spouseName && ` & ${taxReturn.spouseName}`}
          </div>
        </div>
        );
      },
    },
    {
      key: 'taxYear',
      header: columnLabels.taxYear,
      sortable: true,
    },
    {
      key: 'appointmentType',
      header: columnLabels.appointmentType,
      sortable: true,
      render: (taxReturn) => (
        <span style={{ textTransform: 'capitalize' }}>
          {taxReturn.appointmentType.replace('-', ' ')}
        </span>
      ),
    },
    {
      key: 'isAmendment',
      header: columnLabels.isAmendment,
      sortable: true,
      getValue: (taxReturn) => (taxReturn.isAmendment ? 1 : 0),
      render: (taxReturn) =>
        taxReturn.isAmendment ? (
          <Badge appearance="outline" color="danger">{amendmentBadgeLabel(taxReturn.returnType)}</Badge>
        ) : (
          <span style={{ color: tokens.colorNeutralForeground3 }}>-</span>
        ),
    },
    {
      key: 'status',
      header: columnLabels.status,
      sortable: true,
      getValue: (taxReturn) => statusSortOrder[taxReturn.status],
      render: (taxReturn) => (
        <StatusBadge status={taxReturn.status} showIcon showTooltip />
      ),
    },
    {
      key: 'efileStatus',
      header: columnLabels.efileStatus,
      sortable: true,
      getValue: (taxReturn) => {
        const order: Record<string, number> = { pending: 1, submitted: 2, accepted: 3, rejected: 4 };
        return order[taxReturn.efileStatus || ''] || 0;
      },
      render: (taxReturn) => {
        if (!taxReturn.efileStatus || taxReturn.status !== 'e_filed' && taxReturn.status !== 'completed' && taxReturn.status !== 'inactive') return '-';
        const colorMap: Record<string, 'subtle' | 'warning' | 'success' | 'danger'> = {
          pending: 'subtle',
          submitted: 'warning',
          accepted: 'success',
          rejected: 'danger',
        };
        return (
          <Badge appearance="filled" color={colorMap[taxReturn.efileStatus] || 'subtle'}>
            {efileStatusLabels[taxReturn.efileStatus] || taxReturn.efileStatus}
          </Badge>
        );
      },
    },
    {
      key: 'preparerName',
      header: columnLabels.preparerName,
      sortable: true,
      render: (taxReturn) => taxReturn.preparerName || '-',
    },
    {
      key: 'reviewerName',
      header: columnLabels.reviewerName,
      sortable: true,
      render: (taxReturn) => taxReturn.reviewerName || '-',
    },
    {
      key: 'scheduledDate',
      header: columnLabels.scheduledDate,
      sortable: true,
      getValue: (taxReturn) => taxReturn.scheduledDate ? new Date(taxReturn.scheduledDate).getTime() : 0,
      render: (taxReturn) =>
        taxReturn.scheduledDate
          ? format(new Date(taxReturn.scheduledDate), 'MMM d, yyyy')
          : '-',
    },
    {
      key: 'allDocumentsReceived',
      header: columnLabels.allDocumentsReceived,
      sortable: true,
      getValue: (taxReturn) => taxReturn.allDocumentsReceived ? 1 : 0,
      render: (taxReturn) =>
        taxReturn.allDocumentsReceived ? (
          <Badge appearance="filled" color="success">{t('taxReturnsPage.documentsComplete')}</Badge>
        ) : (
          <Badge appearance="filled" color="warning">
            {t('taxReturnsPage.documentsMissing', { count: taxReturn.missingDocuments?.length || 0 })}
          </Badge>
        ),
    },
    {
      key: 'paymentStatus',
      header: columnLabels.paymentStatus,
      sortable: true,
      getValue: (taxReturn) => {
        if (taxReturn.cashPaymentAmount && taxReturn.cashPaymentAmount > 0) return 2;
        if (taxReturn.clientInvoice?.status === 'paid' || taxReturn.invoiceAllocationPaid) return 2;
        if (taxReturn.clientInvoice?.status === 'generated') return 1;
        return 0;
      },
      render: (taxReturn) => {
        if (taxReturn.cashPaymentAmount && taxReturn.cashPaymentAmount > 0) {
          const isZelle = taxReturn.cashPaymentReference
            && taxReturn.cashPaymentReference.toLowerCase().includes('zelle');
          if (isZelle) {
            return <Badge appearance="tint" color="success">{t('taxReturnsPage.zelle')}</Badge>;
          }
          return <Badge appearance="tint" color="warning">{t('taxReturnsPage.cash')}</Badge>;
        }
        if (taxReturn.clientInvoice?.status === 'paid') {
          return <Badge appearance="filled" color="success">{t('taxReturnsPage.paid')}</Badge>;
        }
        if (taxReturn.invoiceAllocationPaid) {
          return <Badge appearance="filled" color="success">{t('taxReturnsPage.paid')}</Badge>;
        }
        if (taxReturn.clientInvoice?.status === 'generated') {
          return <Badge appearance="filled" color="brand">{t('taxReturnsPage.invoiced')}</Badge>;
        }
        return <Badge appearance="filled" color="subtle">{t('taxReturnsPage.notInvoiced')}</Badge>;
      },
    },
    {
      key: '_actions',
      header: '',
      sortable: false,
      render: (taxReturn) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          {activeViewId === 'dropoff-pool' && !taxReturn.preparerId && (
            <Tooltip content={t('taxReturnsPage.claimThisReturn')} relationship="label">
              <Button
                appearance="primary"
                icon={<PersonAvailableRegular />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  claimMutation.mutate(taxReturn);
                }}
                disabled={claimMutation.isPending}
                style={{ minWidth: 'auto' }}
              >
                {t('taxReturnsPage.claim')}
              </Button>
            </Tooltip>
          )}
          <Tooltip content={t('taxReturnsPage.deleteTaxReturn')} relationship="label">
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(taxReturn);
              }}
              style={{ minWidth: 'auto', color: tokens.colorNeutralForeground3 }}
            />
          </Tooltip>
        </div>
      ),
    },
    // Depend on claimMutation.mutate/.isPending (both stable/primitive), NOT the whole claimMutation
    // object (a new ref every render), which would rebuild columns each render and re-open the
    // onPageDataChange render loop. t/columnLabels/efileStatusLabels ARE included -- a language
    // toggle must re-render these headers, not leave them stale until some other dep changes.
    // getTaxReturnDetailHref is a useCallback with no deps, so it's safe to include too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [selectedIds, allPageSelected, somePageSelected, toggleSelect, toggleSelectAll, activeViewId, claimMutation.isPending, claimMutation.mutate, t, columnLabels, efileStatusLabels, getTaxReturnDetailHref]);

  // Reached only for rows getTaxReturnDetailHref returned undefined for (pending_resolution
  // individual returns) -- every other row navigates via the RowLink the clientName column
  // renders, not through this handler.
  const handleRowClick = async (taxReturn: TaxReturn) => {
    setLoadingAttendees(true);
    try {
      const data = await getAppointmentAttendees(taxReturn.appointmentId);
      setAttendeesData(data);
      setResolutionDialogOpen(true);
    } catch (error) {
      console.error('Failed to load attendees:', error);
    } finally {
      setLoadingAttendees(false);
    }
  };

  const handleResolutionComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
    setImportMessage(t('taxReturnsPage.messages.resolved'));
    setTimeout(() => setImportMessage(null), 5000);
  };

  const inactiveCount = (taxReturns || []).filter(tr => tr.status === 'inactive').length;

  if (userLoading || !initialFilterApplied || isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{t('taxReturnsPage.loadFailed')}</p>
        <Button onClick={() => refetch()}>{t('taxReturnsPage.retry')}</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>{t('taxReturnsPage.title')}</h2>
        <p>{t('taxReturnsPage.subtitle')}</p>
      </div>

      {/* Top toolbar: search + tax year (left) → quick-view pills → spacer → action buttons (right). */}
      <div className={bulkStyles.toolbar}>
        <div className={bulkStyles.leadingFilters}>
          <Input
            placeholder={t('taxReturnsPage.searchPlaceholder')}
            value={searchText}
            onChange={(_, data) => setSearchText(data.value)}
            contentBefore={<SearchRegular />}
            style={{ minWidth: '260px' }}
          />

          <Dropdown
            placeholder={t('taxReturnsPage.taxYear')}
            value={selectedYear?.toString() || t('taxReturnsPage.allYears')}
            selectedOptions={selectedYear ? [selectedYear.toString()] : ['']}
            onOptionSelect={(_, data) => setSelectedYear(data.optionValue ? Number(data.optionValue) : undefined)}
            style={{ minWidth: '110px' }}
          >
            <Option text={t('taxReturnsPage.allYears')} value="">{t('taxReturnsPage.allYears')}</Option>
            {yearOptions.map((year) => (
              <Option key={year} text={year.toString()} value={year.toString()}>
                {year}
              </Option>
            ))}
          </Dropdown>
        </div>

        <QuickViewPills views={QUICK_VIEWS} activeKey={activeViewId} onSelect={(key) => list.selectView(key)} />

        <div className={bulkStyles.toolbarSpacer} />

        {/* Wave 3 (2026-11-02): saved views, on the same core.SavedView/StaffScreenState stores
            Client Outreach uses -- ported UI, same i18n namespace (the strings are page-agnostic
            chrome: "Views", "Save current view...", not "Client Outreach"-specific wording). */}
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button appearance="subtle" icon={<BookmarkRegular />}>
              {t('outreachPage.savedViews.menuButton')}
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {taxReturnsSavedViews.length === 0 && (
                <MenuItem disabled>{t('outreachPage.savedViews.none')}</MenuItem>
              )}
              {taxReturnsSavedViews.map((view) => (
                <MenuItem
                  key={view.id}
                  icon={view.isDefault ? <StarFilled /> : undefined}
                  onClick={() => applyTaxReturnsSavedView(view)}
                  secondaryContent={
                    <span style={{ display: 'inline-flex', gap: '4px' }}>
                      <Button
                        appearance="transparent"
                        size="small"
                        icon={view.isDefault ? <StarFilled /> : <StarRegular />}
                        title={t('outreachPage.savedViews.setDefault')}
                        onClick={(e) => { e.stopPropagation(); handleSetDefaultTaxReturnsView(view); }}
                      />
                      {view.canEdit && (
                        <Button
                          appearance="transparent"
                          size="small"
                          icon={<DeleteRegular />}
                          title={t('outreachPage.savedViews.delete')}
                          onClick={(e) => { e.stopPropagation(); handleDeleteTaxReturnsView(view.id); }}
                        />
                      )}
                    </span>
                  }
                >
                  {view.visibility === 'team' && !view.isOwner && view.ownerName
                    ? `${view.name} — ${view.ownerName}`
                    : view.name}
                </MenuItem>
              ))}
              <MenuDivider />
              <MenuItem icon={<SaveRegular />} onClick={() => setSaveViewDialogOpen(true)}>
                {t('outreachPage.savedViews.saveCurrent')}
              </MenuItem>
              <MenuItem onClick={resetTaxReturnsScreenState}>
                {t('outreachPage.savedViews.resetToDefault')}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>

        <Dialog open={saveViewDialogOpen} onOpenChange={(_, data) => setSaveViewDialogOpen(data.open)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{t('outreachPage.savedViews.saveDialogTitle')}</DialogTitle>
              <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Input
                    value={newViewName}
                    onChange={(_, data) => setNewViewName(data.value)}
                    placeholder={t('outreachPage.savedViews.namePlaceholder')}
                  />
                  <Checkbox
                    checked={newViewShared}
                    onChange={(_, data) => setNewViewShared(!!data.checked)}
                    label={t('outreachPage.savedViews.shareToTeam')}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setSaveViewDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button appearance="primary" disabled={!newViewName.trim()} onClick={handleSaveTaxReturnsView}>
                  {t('outreachPage.savedViews.save')}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {/* Column chooser */}
        <Popover>
          <PopoverTrigger disableButtonEnhancement>
            <Tooltip content={t('taxReturnsPage.toggleColumns')} relationship="label">
              <Button appearance="subtle" icon={<ColumnTripleRegular />}>
                <EyeRegular style={{ marginLeft: '4px' }} />
                <Badge appearance="filled" size="small" style={{ marginLeft: '4px' }}>
                  {visibleColumns.length}/{COLUMN_DEFINITIONS.length}
                </Badge>
              </Button>
            </Tooltip>
          </PopoverTrigger>
          <PopoverSurface style={{ padding: '12px', minWidth: '200px' }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px' }}>{t('taxReturnsPage.visibleColumns')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {COLUMN_DEFINITIONS.map((colKey) => (
                <Checkbox
                  key={colKey}
                  checked={visibleColumns.includes(colKey)}
                  onChange={() => toggleColumn(colKey)}
                  label={columnLabels[colKey]}
                />
              ))}
            </div>
          </PopoverSurface>
        </Popover>

        <Tooltip
          content={showInactive ? t('taxReturnsPage.hideInactiveReturns') : t('taxReturnsPage.showInactiveReturns')}
          relationship="label"
        >
          <Button
            appearance={showInactive ? 'primary' : 'subtle'}
            onClick={() => setShowInactive(!showInactive)}
          >
            {t('taxReturnsPage.showInactive')} {inactiveCount > 0 && (
              <Badge
                appearance="filled"
                color={showInactive ? 'informative' : 'subtle'}
                style={{ marginLeft: '8px' }}
              >
                {inactiveCount}
              </Badge>
            )}
          </Button>
        </Tooltip>

        <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={clearFilters}>
          {t('taxReturnsPage.clearFilters')}
        </Button>

        <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={() => refetch()}>
          {t('taxReturnsPage.refresh')}
        </Button>

        <Button appearance="subtle" icon={<DocumentArrowDownRegular />} onClick={handleExport}>
          {t('taxReturnsPage.exportExcel')}
        </Button>

        <Button appearance="primary" onClick={() => setShowDropoffDialog(true)}>
          {t('taxReturnsPage.addDropoff')}
        </Button>

        <Button
          appearance="primary"
          icon={<ArrowDownloadRegular />}
          onClick={() => importMutation.mutate()}
          disabled={importMutation.isPending}
        >
          {importMutation.isPending ? t('taxReturnsPage.importing') : t('taxReturnsPage.importFromAppointments')}
        </Button>

        <Button
          appearance="secondary"
          icon={<ArrowClockwiseRegular />}
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? t('taxReturnsPage.syncing') : t('taxReturnsPage.syncClientInfo')}
        </Button>
      </div>

      <ActiveFiltersChips columns={filterColumns} value={list.filters} onChange={list.setFilters} />

      {importMessage && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          backgroundColor: importMessage.startsWith('Error') ? '#fde7e9' : '#dff6dd',
          borderRadius: '4px',
          color: importMessage.startsWith('Error') ? '#a4262c' : '#107c10'
        }}>
          {importMessage}
        </div>
      )}

      <div className="card">
        <DataTable
          data={displayData}
          columns={columns}
          visibleColumns={['_select', ...visibleColumns, '_actions']}
          rowKey="id"
          searchable
          searchText={searchText}
          onSearchTextChange={setSearchText}
          searchKeys={SEARCH_KEYS}
          sortColumn={list.sort?.col ?? 'scheduledDate'}
          sortDirection={list.sort?.dir ?? 'asc'}
          onSortChange={(next) => list.setSort(next ? { col: String(next.column), dir: next.direction } : null)}
          renderHeaderExtra={renderHeaderExtra}
          onRowClick={handleRowClick}
          getRowHref={getTaxReturnDetailHref}
          onPageDataChange={setPageItems}
          emptyMessage={t('taxReturnsPage.emptyMessage')}
          loading={isLoading}
          paginated
          groupBy={list.group}
          groupGranularity={list.groupGranularity ?? undefined}
        />
      </div>

      {/* Bulk message */}
      {bulkMessage && (
        <div className={bulkStyles.bulkMessage} style={{
          backgroundColor: bulkMessage.startsWith('Error') ? '#fde7e9' : '#dff6dd',
          color: bulkMessage.startsWith('Error') ? '#a4262c' : '#107c10',
        }}>
          {bulkMessage}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className={bulkStyles.bulkBar}>
          <SelectAllOnRegular />
          <span className={bulkStyles.bulkCount}>{t('taxReturnsPage.selectedCount', { count: selectedIds.size })}</span>
          <Dropdown
            placeholder={t('taxReturnsPage.changeStatus')}
            value={bulkStatus ? statusDisplayNames[bulkStatus as TaxReturnStatus] : ''}
            selectedOptions={bulkStatus ? [bulkStatus] : []}
            onOptionSelect={(_, data) => setBulkStatus((data.optionValue as TaxReturnStatus) || '')}
            style={{ minWidth: '180px', backgroundColor: 'white' }}
          >
            {bulkStatusOptions.map((s) => (
              <Option key={s} text={statusDisplayNames[s]} value={s}>
                {statusDisplayNames[s]}
              </Option>
            ))}
          </Dropdown>
          <Dropdown
            placeholder={t('taxReturnsPage.efileStatusPlaceholder')}
            value={bulkEfileStatus ? efileStatusLabels[bulkEfileStatus] || bulkEfileStatus : ''}
            selectedOptions={bulkEfileStatus ? [bulkEfileStatus] : []}
            onOptionSelect={(_, data) => setBulkEfileStatus(data.optionValue || '')}
            style={{ minWidth: '160px', backgroundColor: 'white' }}
          >
            {efileStatusOptions.map((s) => (
              <Option key={s} text={efileStatusLabels[s]} value={s}>
                {efileStatusLabels[s]}
              </Option>
            ))}
          </Dropdown>
          <Button
            appearance="primary"
            onClick={handleBulkApply}
            disabled={(!bulkStatus && !bulkEfileStatus) || bulkMutation.isPending}
            style={{ backgroundColor: 'white', color: tokens.colorBrandBackground }}
          >
            {bulkMutation.isPending ? t('taxReturnsPage.updating') : t('taxReturnsPage.apply')}
          </Button>
          <Tooltip content={t('taxReturnsPage.markInactiveTooltip')} relationship="label">
            <Button
              appearance="subtle"
              icon={<PersonDeleteRegular />}
              onClick={handleMarkAsInactive}
              disabled={bulkMutation.isPending}
              style={{ color: 'white' }}
            >
              {t('taxReturnsPage.markInactive')}
            </Button>
          </Tooltip>
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={() => { setSelectedIds(new Set()); setBulkStatus(''); setBulkEfileStatus(''); }}
            style={{ color: 'white' }}
          >
            {t('taxReturnsPage.clear')}
          </Button>
        </div>
      )}

      {/* Loading overlay for attendees */}
      {loadingAttendees && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Spinner size="large" label={t('taxReturnsPage.loadingAttendees')} />
        </div>
      )}

      {/* Attendee Resolution Dialog */}
      {attendeesData && (
        <AttendeeResolutionDialog
          open={resolutionDialogOpen}
          onOpenChange={setResolutionDialogOpen}
          attendeesData={attendeesData}
          taxYear={selectedYear || defaultTaxYear}
          onResolved={handleResolutionComplete}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(_, data) => { if (!data.open) setDeleteTarget(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('taxReturnsPage.deleteDialog.title')}</DialogTitle>
            <DialogContent>
              <p>
                <Trans
                  i18nKey="taxReturnsPage.deleteDialog.body"
                  values={{ clientName: deleteTarget?.clientName, taxYear: deleteTarget?.taxYear }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p style={{ color: tokens.colorPaletteRedForeground1, fontSize: '13px', marginTop: '8px' }}>
                {t('taxReturnsPage.deleteDialog.warning')}
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteTarget(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget.id); }}
                disabled={deleteMutation.isPending}
                style={{ backgroundColor: tokens.colorPaletteRedBackground3, borderColor: tokens.colorPaletteRedBackground3 }}
              >
                {deleteMutation.isPending ? t('taxReturnsPage.deleteDialog.deleting') : t('taxReturnsPage.deleteDialog.delete')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <CreateDropoffReturnDialog
        open={showDropoffDialog}
        onOpenChange={setShowDropoffDialog}
        preparerId={preparerId}
        preparerName={preparerName}
      />
    </div>
  );
}
